import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Cart drawer "People also bought" upsell.
 *
 * Responsibilities are deliberately narrow:
 *   - carousel navigation (the cards themselves are server-rendered Liquid)
 *   - variant selection for multi-variant recommendations
 *   - direct add-to-cart via the Shopify AJAX Cart API
 *
 * It does NOT re-render itself. Adding a line dispatches the theme's standard
 * `CartLinesUpdateEvent`, exactly like `assets/product-form.js` does, and
 * `assets/component-cart-items.js` morphs the whole `cart-drawer-section` from
 * the sections payload. That single mechanism keeps the line items, the cart
 * total, the free shipping progress bar and these recommendations in sync
 * without a page reload and without a second update path.
 */
class LvCartUpsell extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** Guards against duplicate submits while a request is in flight. */
  #busy = false;
  #resizeObserver = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Delegated listeners on the host element: survives morphs of the inner
    // markup and can never bind twice for the same element instance.
    this.addEventListener('click', this.#handleClick, { signal });
    this.#resizeObserver = new ResizeObserver(this.#syncArrows);
    this.#resizeObserver.observe(this);

    const track = this.#track;
    if (track) {
      track.addEventListener('scroll', this.#syncArrows, { signal, passive: true });
    }

    this.#syncArrows();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
  }

  /** @returns {HTMLElement | null} */
  get #track() {
    return this.querySelector('[data-lv-upsell-track]');
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-lv-upsell-prev]')) {
      event.preventDefault();
      this.#scrollByCard(-1);
      return;
    }

    if (target.closest('[data-lv-upsell-next]')) {
      event.preventDefault();
      this.#scrollByCard(1);
      return;
    }

    const choice = target.closest('[data-lv-upsell-option]');
    if (choice instanceof HTMLButtonElement && !choice.disabled) {
      event.preventDefault();
      this.#selectVariant(choice);
      return;
    }

    const addButton = target.closest('[data-lv-upsell-add]');
    if (addButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#addToCart(addButton);
    }
  };

  #selectVariant(choice) {
    const card = choice.closest('[data-lv-upsell-card]');
    if (!card || this.#busy) return;
    card.dataset.variantId = choice.dataset.variantId;
    for (const option of card.querySelectorAll('[data-lv-upsell-option]')) {
      option.setAttribute('aria-pressed', String(option === choice));
    }
    const price = card.querySelector('[data-lv-upsell-price]');
    const compare = card.querySelector('[data-lv-upsell-compare]');
    const image = card.querySelector('.lv-upsell__image');
    if (price) price.textContent = choice.dataset.price;
    if (compare) {
      compare.textContent = choice.dataset.compare || '';
      compare.hidden = !choice.dataset.compare;
    }
    if (image && choice.dataset.image) {
      image.removeAttribute('srcset');
      image.src = choice.dataset.image;
    }
    this.#clearError(card);
  }

  /** @param {number} direction -1 for previous, 1 for next. */
  #scrollByCard(direction) {
    const track = this.#track;
    if (!track) return;

    const card = track.querySelector('[data-lv-upsell-card]');
    const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
    const step = card instanceof HTMLElement ? card.getBoundingClientRect().width + gap : track.clientWidth;

    track.scrollBy({ left: direction * step, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  #syncArrows = () => {
    const track = this.#track;
    const prev = this.querySelector('[data-lv-upsell-prev]');
    const next = this.querySelector('[data-lv-upsell-next]');
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= maxScroll - 1;

    if (prev instanceof HTMLButtonElement) prev.disabled = atStart;
    if (next instanceof HTMLButtonElement) next.disabled = atEnd || maxScroll <= 1;
  };

  /**
   * Collects the section IDs every cart-items-component on the page needs,
   * mirroring `assets/component-cart-items.js`, so one add request refreshes
   * the drawer (and the cart page markup, when both are mounted).
   * @returns {string}
   */
  #sectionIds() {
    const ids = new Set();
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        ids.add(component.dataset.sectionId);
      }
    }
    return Array.from(ids).join(',');
  }

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    if (this.#busy) return;

    const card = button.closest('[data-lv-upsell-card]');
    if (!(card instanceof HTMLElement)) return;

    const variantId = card.dataset.variantId;
    if (!variantId) {
      this.#showError(card, this.dataset.msgChoose || 'Please choose an option.');
      return;
    }

    this.#busy = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.#clearError(card);

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          sections: this.#sectionIds(),
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json();

      // Shopify returns a `status` field only on failure (e.g. 422 sold out).
      if (result.status) {
        const cart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'lv-cart-upsell',
            didError: true,
          },
        });
        this.#showError(card, result.description || result.message || this.dataset.msgAddError || 'Could not add this item.');
        return;
      }

      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: 1,
          source: 'lv-cart-upsell',
          sections: result.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.#showError(card, this.dataset.msgGenericError || 'Something went wrong. Please try again.');
      if (error?.name !== 'AbortError') console.warn('[lv-cart-upsell] Add to cart failed:', error);
    } finally {
      this.#busy = false;
      // The drawer usually re-renders and replaces this button; restoring state
      // matters for the error paths, where the old markup stays on screen.
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  /** @returns {Promise<any>} */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * @param {HTMLElement} card
   * @param {string} message
   */
  #showError(card, message) {
    const errorEl = card.querySelector('[data-lv-upsell-error]');
    if (!(errorEl instanceof HTMLElement)) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
    window.setTimeout(() => {
      if (errorEl.isConnected) this.#clearError(card);
    }, 4000);
  }

  /** @param {HTMLElement} card */
  #clearError(card) {
    const errorEl = card.querySelector('[data-lv-upsell-error]');
    if (!(errorEl instanceof HTMLElement)) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }
}

if (!customElements.get('lv-cart-upsell')) {
  customElements.define('lv-cart-upsell', LvCartUpsell);
}